import React, { useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faArrowDown, faArrowUp, faPlus, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';

import { backgroundGrey, grape, grey, lighterGrey, salmon, white } from 'lib/css/colors';
import { CLIENT_GLOBAL_VENDOR } from 'lib/networking/endpoints';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import {
  MEDIA_TYPE_OPTIONS,
  PRODUCT_FACING_OPTIONS,
  PRODUCT_STATE_OPTIONS,
} from 'lib/enums/ImageCategoryEnums';
import pointerOnHover from 'lib/css/pointerOnHover';
import { SwalToast } from 'lib/utils/toast';
import usePut from 'lib/hooks/usePut';
import useGet from 'lib/hooks/useGet';

function ImageCategoryModal({ show, onHide, selectedCategory, isNewCategory, vendorId }) {
  const [settings, setSettings] = useState([]);
  const [thumbnailSetting, setThumbnailSetting] = useState({
    mediaType: null,
    productState: null,
    productFacing: null,
  });
  const [hasInitialized, setHasInitialized] = useState(false);
  const [newSetting, setNewSetting] = useState({
    mediaType: null,
    productState: null,
    productFacing: null,
  });

  const categoryKey = selectedCategory?.value;

  // Fetch existing client_global_vendor configs
  const {
    data: { data: clientGlobalVendor } = {},
    loading,
    refetch,
  } = useGet(
    CLIENT_GLOBAL_VENDOR,
    {
      client_id: CUT_DRY_CLIENT_ID,
      global_vendor_id: vendorId,
    },
    !vendorId || !show,
  );

  // Reset initialization flag when modal opens or category changes
  useEffect(() => {
    if (show) {
      setHasInitialized(false);
    }
  }, [show, categoryKey]);

  // Extract settings from configs when data loads (only on initial load)
  useEffect(() => {
    if (!hasInitialized && clientGlobalVendor !== undefined && categoryKey) {
      const imageCategorySettings = clientGlobalVendor?.configs?.image_category_setting || {};
      const categorySettings = imageCategorySettings[categoryKey] || [];

      // Add unique IDs for tracking
      const settingsWithIds = categorySettings.map((setting, index) => ({
        id: `${Date.now()}-${index}`,
        mediaType: setting.media_type,
        productState: setting.product_state,
        productFacing: setting.product_facing,
      }));
      setSettings(settingsWithIds);

      // Extract thumbnail setting
      const thumbnailSettings = clientGlobalVendor?.configs?.thumbnail_setting || {};
      const categoryThumbnailSetting = thumbnailSettings[categoryKey];

      if (categoryThumbnailSetting) {
        setThumbnailSetting({
          mediaType: categoryThumbnailSetting.media_type,
          productState: categoryThumbnailSetting.product_state,
          productFacing: categoryThumbnailSetting.product_facing,
        });
      } else {
        setThumbnailSetting({
          mediaType: null,
          productState: null,
          productFacing: null,
        });
      }

      setHasInitialized(true);
    }
  }, [clientGlobalVendor, categoryKey, hasInitialized]);

  // Update configs using PUT
  const { putData: updateConfigs, loading: saving } = usePut(
    CLIENT_GLOBAL_VENDOR,
    () => {
      SwalToast('success', 'Settings saved successfully');
      setHasInitialized(false); // Reset so next refetch updates the state
      refetch();
      onHide(); // Close the modal after successful save
    },
    () => SwalToast('error', 'Failed to save settings'),
  );

  // Save current settings to backend (only called when Save button is clicked)
  const handleSave = () => {
    if (!vendorId || !categoryKey) return;

    // Build the image_category_setting structure
    const settingsArray = settings.map(s => ({
      media_type: s.mediaType,
      product_state: s.productState,
      product_facing: s.productFacing,
    }));

    // Get existing configs and merge
    const existingConfigs = clientGlobalVendor?.configs || {};
    const existingImageCategorySettings = existingConfigs.image_category_setting || {};
    const existingThumbnailSettings = existingConfigs.thumbnail_setting || {};

    // Build thumbnail setting object (only if all fields are set)
    const newThumbnailSettings = { ...existingThumbnailSettings };

    if (
      thumbnailSetting.mediaType &&
      thumbnailSetting.productState &&
      thumbnailSetting.productFacing
    ) {
      newThumbnailSettings[categoryKey] = {
        media_type: thumbnailSetting.mediaType,
        product_state: thumbnailSetting.productState,
        product_facing: thumbnailSetting.productFacing,
      };
    } else {
      // Remove thumbnail setting for this category if not all fields are set
      delete newThumbnailSettings[categoryKey];
    }

    const newConfigs = {
      ...existingConfigs,
      image_category_setting: {
        ...existingImageCategorySettings,
        [categoryKey]: settingsArray,
      },
      thumbnail_setting: newThumbnailSettings,
    };

    updateConfigs({
      client_id: CUT_DRY_CLIENT_ID,
      global_vendor_id: parseInt(vendorId, 10),
      configs: newConfigs,
    });
  };

  const handleAddSetting = () => {
    if (!newSetting.mediaType || !newSetting.productState || !newSetting.productFacing) {
      SwalToast('warning', 'Please select all fields');
      return;
    }

    const newEntry = {
      id: `${Date.now()}-${settings.length}`,
      mediaType: newSetting.mediaType.value,
      productState: newSetting.productState.value,
      productFacing: newSetting.productFacing.value,
    };

    setSettings(prevSettings => [...prevSettings, newEntry]);
    setNewSetting({ mediaType: null, productState: null, productFacing: null });
  };

  const handleDeleteSetting = id => {
    setSettings(prevSettings => prevSettings.filter(s => s.id !== id));
  };

  const handleMoveUp = index => {
    if (index === 0) return; // Already at the top

    setSettings(prevSettings => {
      const newSettings = [...prevSettings];
      // Swap with the item above
      [newSettings[index - 1], newSettings[index]] = [newSettings[index], newSettings[index - 1]];
      return newSettings;
    });
  };

  const handleMoveDown = index => {
    if (index === settings.length - 1) return; // Already at the bottom

    setSettings(prevSettings => {
      const newSettings = [...prevSettings];
      // Swap with the item below
      [newSettings[index], newSettings[index + 1]] = [newSettings[index + 1], newSettings[index]];
      return newSettings;
    });
  };

  const getOptionLabel = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option?.label || value;
  };

  const getOptionFromValue = (options, value) => {
    return options.find(opt => opt.value === value) || null;
  };

  const isLoading = loading || saving;

  return (
    <Modal centered show={show} onHide={onHide} size="lg" dialogClassName={css(styles.modalDialog)}>
      <Modal.Header closeButton className={css(styles.header)}>
        <Modal.Title className="font-weight-bold">
          {isNewCategory ? 'Create New Image Category' : 'Image Category Settings'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={css(styles.body)}>
        {/* Category Name Display */}
        <Form.Group className="mb-4">
          <Form.Label className={css(styles.label)}>Category Name</Form.Label>
          <Form.Control
            type="text"
            value={selectedCategory?.label || ''}
            readOnly
            className={css(styles.readonlyInput)}
          />
        </Form.Group>

        {/* Thumbnail Setting Section */}
        <div className={css(styles.thumbnailSection)}>
          <h6 className={css(styles.sectionTitle)}>Thumbnail Setting</h6>
          <p className={css(styles.sectionDescription)}>
            Configure the default thumbnail image criteria for this category. This setting is
            optional.
          </p>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className={css(styles.label)}>Media Type</Form.Label>
                <Select
                  options={MEDIA_TYPE_OPTIONS}
                  value={getOptionFromValue(MEDIA_TYPE_OPTIONS, thumbnailSetting.mediaType)}
                  onChange={option =>
                    setThumbnailSetting({ ...thumbnailSetting, mediaType: option?.value || null })
                  }
                  placeholder="Select..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className={css(styles.label)}>Product State</Form.Label>
                <Select
                  options={PRODUCT_STATE_OPTIONS}
                  value={getOptionFromValue(PRODUCT_STATE_OPTIONS, thumbnailSetting.productState)}
                  onChange={option =>
                    setThumbnailSetting({
                      ...thumbnailSetting,
                      productState: option?.value || null,
                    })
                  }
                  placeholder="Select..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className={css(styles.label)}>Product Facing</Form.Label>
                <Select
                  options={PRODUCT_FACING_OPTIONS}
                  value={getOptionFromValue(PRODUCT_FACING_OPTIONS, thumbnailSetting.productFacing)}
                  onChange={option =>
                    setThumbnailSetting({
                      ...thumbnailSetting,
                      productFacing: option?.value || null,
                    })
                  }
                  placeholder="Select..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Add New Setting Section */}
        <div className={css(styles.addSection)}>
          <h6 className={css(styles.sectionTitle)}>
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add New Setting
          </h6>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label className={css(styles.label)}>Media Type</Form.Label>
                <Select
                  options={MEDIA_TYPE_OPTIONS}
                  value={newSetting.mediaType}
                  onChange={option => setNewSetting({ ...newSetting, mediaType: option })}
                  placeholder="Select..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label className={css(styles.label)}>Product State</Form.Label>
                <Select
                  options={PRODUCT_STATE_OPTIONS}
                  value={newSetting.productState}
                  onChange={option => setNewSetting({ ...newSetting, productState: option })}
                  placeholder="Select..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label className={css(styles.label)}>Product Facing</Form.Label>
                <Select
                  options={PRODUCT_FACING_OPTIONS}
                  value={newSetting.productFacing}
                  onChange={option => setNewSetting({ ...newSetting, productFacing: option })}
                  placeholder="Select..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button variant="primary" onClick={handleAddSetting} disabled={isLoading}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add
              </Button>
            </Col>
          </Row>
        </div>

        {/* Existing Settings Table */}
        <div className={css(styles.tableSection)}>
          <h6 className={css(styles.sectionTitle)}>
            Current Settings
            <span className={css(styles.hint)}>(Use arrows to reorder)</span>
          </h6>

          {isLoading && <LoadingSpinner short />}

          {!isLoading && settings.length === 0 && (
            <div className={css(styles.emptyState)}>
              No settings configured for this category. Add one above.
            </div>
          )}

          {!isLoading && settings.length > 0 && (
            <>
              {/* Header Row */}
              <Row className={css(styles.gridHeader)}>
                <Col xs={1}>#</Col>
                <Col xs={3}>Media Type</Col>
                <Col xs={3}>Product State</Col>
                <Col xs={3}>Product Facing</Col>
                <Col xs={2} className="text-center">
                  Actions
                </Col>
              </Row>
              {/* Data Rows */}
              {settings.map((setting, index) => (
                <Row key={setting.id} className={css(styles.gridRow)}>
                  <Col xs={1} className={css(styles.orderColumn)}>
                    {index + 1}
                  </Col>
                  <Col xs={3}>{getOptionLabel(MEDIA_TYPE_OPTIONS, setting.mediaType)}</Col>
                  <Col xs={3}>{getOptionLabel(PRODUCT_STATE_OPTIONS, setting.productState)}</Col>
                  <Col xs={3}>{getOptionLabel(PRODUCT_FACING_OPTIONS, setting.productFacing)}</Col>
                  <Col xs={2} className="text-center">
                    <div className={css(styles.actionButtons)}>
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className={`${css(styles.arrowIcon, index === 0 && styles.disabledIcon)} ${
                          index === 0 ? '' : pointerOnHover
                        }`}
                        onClick={() => handleMoveUp(index)}
                        title="Move up"
                      />
                      <FontAwesomeIcon
                        icon={faArrowDown}
                        className={`${css(
                          styles.arrowIcon,
                          index === settings.length - 1 && styles.disabledIcon,
                        )} ${index === settings.length - 1 ? '' : pointerOnHover}`}
                        onClick={() => handleMoveDown(index)}
                        title="Move down"
                      />
                      <FontAwesomeIcon
                        icon={faTrash}
                        className={`${css(styles.deleteIcon)} ${pointerOnHover}`}
                        onClick={() => handleDeleteSetting(setting.id)}
                        title="Delete"
                      />
                    </div>
                  </Col>
                </Row>
              ))}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className={css(styles.footer)}>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={faSave} className="mr-2" />
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? grape : lighterGrey,
    boxShadow: state.isFocused ? `0 0 0 1px ${grape}` : 'none',
    '&:hover': {
      borderColor: grape,
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? grape : state.isFocused ? backgroundGrey : white,
    color: state.isSelected ? white : 'inherit',
  }),
};

const styles = StyleSheet.create({
  modalDialog: {
    maxWidth: '800px',
  },
  header: {
    borderBottom: `2px solid ${lighterGrey}`,
  },
  body: {
    padding: '24px',
  },
  label: {
    fontWeight: 600,
    fontSize: '13px',
    color: grey,
    marginBottom: '4px',
  },
  readonlyInput: {
    backgroundColor: `${backgroundGrey} !important`,
    cursor: 'not-allowed',
  },
  thumbnailSection: {
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '16px',
    border: `1px solid ${lighterGrey}`,
  },
  addSection: {
    padding: '16px',
    backgroundColor: backgroundGrey,
    borderRadius: '8px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '12px',
  },
  sectionDescription: {
    fontSize: '12px',
    color: grey,
    marginBottom: '12px',
  },
  hint: {
    fontWeight: 400,
    fontSize: '12px',
    color: grey,
    marginLeft: '8px',
  },
  tableSection: {
    marginTop: '16px',
  },
  gridHeader: {
    backgroundColor: backgroundGrey,
    padding: '10px 0',
    fontWeight: 600,
    fontSize: '14px',
    borderBottom: `1px solid ${lighterGrey}`,
    margin: 0,
  },
  gridRow: {
    backgroundColor: white,
    padding: '12px 0',
    margin: 0,
    borderBottom: `1px solid ${lighterGrey}`,
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: backgroundGrey,
    },
  },
  orderColumn: {
    fontWeight: 600,
    color: grey,
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
  },
  arrowIcon: {
    color: grape,
    fontSize: '14px',
    transition: 'color 0.2s ease',
    ':hover': {
      color: '#6b2d8f',
    },
  },
  disabledIcon: {
    color: lighterGrey,
    cursor: 'not-allowed',
    ':hover': {
      color: lighterGrey,
    },
  },
  deleteIcon: {
    color: salmon,
    fontSize: '14px',
    transition: 'color 0.2s ease',
    ':hover': {
      color: '#d63031',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: backgroundGrey,
    borderRadius: '8px',
    color: grey,
    fontSize: '14px',
  },
  footer: {
    borderTop: `2px solid ${lighterGrey}`,
  },
});

ImageCategoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedCategory: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    __isNew__: PropTypes.bool,
  }),
  isNewCategory: PropTypes.bool,
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ImageCategoryModal;
